'use client';
import React, { useState } from 'react';

import { useParams, useRouter } from 'next/navigation';
import { products } from '@/data/products';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function CheckoutPage() {
  const params = useParams();
  const router = useRouter();
  const id = Number(params?.id);
  const product = products?.find((p) => p?.id === id) || products?.[0];

  const [step, setStep] = useState<'address' | 'payment'>('address');
  const [form, setForm] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });

  const isFormValid = form?.name && form?.phone && form?.address && form?.city && form?.state && form?.pincode;

  const mrp = product?.originalPrice;
  const discount = mrp - product?.price;
  const creditDiscount = Math.round(product?.price * 0.1);
  const toPay = product?.price - creditDiscount;

  return (
    <div>
      <Navbar />
      <div className="pt-14 md:pt-16 min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
          {/* Back */}
          <button
            onClick={() => step === 'payment' ? setStep('address') : window.history?.back()}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0c831f] transition-colors mb-4"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to cart
          </button>

          {/* Delivery Info */}
          <div className="bg-[#0c831f] rounded-2xl p-4 flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <div>
                <p className="text-white font-bold text-sm">Delivery in 4 hours</p>
                <p className="text-white/70 text-xs">Wednesday, 8 July</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-white/80 text-xs">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Kolkata
            </div>
          </div>

          {/* Steps */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep('address')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  step === 'address' ? 'bg-[#0c831f] text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-extrabold ${
                  step === 'address' ? 'bg-white text-[#0c831f]' : 'bg-gray-400 text-white'
                }`}>1</span>
                Address
              </button>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-300">
                <path d="m9 18 6-6-6-6" />
              </svg>
              <button
                onClick={() => isFormValid && setStep('payment')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  step === 'payment' ? 'bg-[#0c831f] text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-extrabold ${
                  step === 'payment' ? 'bg-white text-[#0c831f]' : 'bg-gray-400 text-white'
                }`}>2</span>
                Payment
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left: Form */}
            <div className="md:col-span-2">
              {step === 'address' ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0c831f]">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <h2 className="text-lg font-extrabold text-gray-900">Delivery address</h2>
                  </div>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Full name (first and last) *"
                      value={form?.name}
                      onChange={(e) => setForm({ ...form, name: e?.target?.value })}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                    />
                    <div className="flex gap-3">
                      <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                        <span className="px-3 py-3 text-sm font-semibold text-gray-600 bg-gray-50 border-r border-gray-200">+91</span>
                        <input
                          type="tel"
                          placeholder="Mobile number *"
                          value={form?.phone}
                          onChange={(e) => setForm({ ...form, phone: e?.target?.value })}
                          className="flex-1 px-4 py-3 text-sm outline-none"
                        />
                      </div>
                    </div>
                    <input
                      type="text"
                      placeholder="House / flat / floor, street, area *"
                      value={form?.address}
                      onChange={(e) => setForm({ ...form, address: e?.target?.value })}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="City *"
                        value={form?.city}
                        onChange={(e) => setForm({ ...form, city: e?.target?.value })}
                        className="border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                      />
                      <input
                        type="text"
                        placeholder="State *"
                        value={form?.state}
                        onChange={(e) => setForm({ ...form, state: e?.target?.value })}
                        className="border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Pincode *"
                      value={form?.pincode}
                      onChange={(e) => setForm({ ...form, pincode: e?.target?.value })}
                      className="w-full md:w-1/2 border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                    />
                    <button
                      onClick={() => isFormValid && setStep('payment')}
                      disabled={!isFormValid}
                      className={`w-full py-3.5 rounded-xl font-extrabold text-sm transition-all ${
                        isFormValid
                          ? 'bg-[#0c831f] text-white hover:bg-[#065f12] active:scale-95'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Continue to payment →
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="text-2xl">💳</span>
                    <h2 className="text-lg font-extrabold text-gray-900">Pay by Card</h2>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Card Number</label>
                      <input
                        type="text"
                        placeholder="1234 5678 9012 3456"
                        maxLength={19}
                        onChange={(e) => {
                          const val = e?.target?.value?.replace(/\D/g, '')?.slice(0, 16);
                          e.target.value = val?.replace(/(.{4})/g, '$1 ')?.trim();
                        }}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Cardholder Name</label>
                      <input
                        type="text"
                        placeholder="Name as on card"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Expiry Date</label>
                        <input
                          type="text"
                          placeholder="MM / YY"
                          maxLength={7}
                          onChange={(e) => {
                            const val = e?.target?.value?.replace(/\D/g, '')?.slice(0, 4);
                            e.target.value = val?.length > 2 ? val?.slice(0, 2) + ' / ' + val?.slice(2) : val;
                          }}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">CVV</label>
                        <input
                          type="password"
                          placeholder="•••"
                          maxLength={4}
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0c831f] transition-colors"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                      <span className="text-lg">🔒</span>
                      <p className="text-xs text-gray-500">Your card details are encrypted and secure. We never store your CVV.</p>
                    </div>
                  </div>
                  <button className="w-full mt-6 py-4 rounded-2xl bg-[#0c831f] text-white font-extrabold text-sm hover:bg-[#065f12] transition-colors active:scale-95 shadow-lg"
                    onClick={() => router?.push('/thank-you')}
                  >
                    Pay ₹{toPay} Securely →
                  </button>
                  <p className="text-xs text-gray-400 text-center mt-3">🔒 Secured by SSL • 100% Safe Payment</p>
                </div>
              )}
            </div>

            {/* Right: Order Summary */}
            <div className="flex flex-col gap-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-base font-extrabold text-gray-900 mb-4">Order summary</h3>
                <div className="flex items-start gap-3 mb-4">
                  <img
                    alt={product?.name}
                    src={product?.image}
                    className="w-14 h-14 object-contain rounded-xl border border-gray-100 bg-gray-50 flex-shrink-0"
                  />
                  <div>
                    <p className="text-xs font-semibold text-gray-800 line-clamp-2">{product?.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Qty: 1</p>
                  </div>
                </div>

                <h3 className="text-base font-extrabold text-gray-900 mb-3 border-t border-gray-100 pt-4">Bill details</h3>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">MRP</span>
                    <span className="font-semibold">₹{mrp?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Product discount</span>
                    <span className="font-semibold text-green-600">− ₹{discount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Credit card (10%)</span>
                    <span className="font-semibold text-green-600">− ₹{creditDiscount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery fee</span>
                    <span className="font-semibold text-green-600">Free</span>
                  </div>
                  <div className="border-t border-gray-100 pt-2.5 flex justify-between">
                    <span className="font-extrabold text-gray-900">To pay</span>
                    <span className="font-extrabold text-gray-900">₹{toPay}</span>
                  </div>
                </div>
                <div className="mt-3 bg-green-50 rounded-xl px-3 py-2 text-xs text-green-700 font-semibold text-center">
                  🎉 You&apos;re saving ₹{(discount + creditDiscount)?.toLocaleString()} on this order!
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 grid grid-cols-2 gap-3">
                {[
                  { icon: '✅', label: '100% Genuine' },
                  { icon: '🚚', label: 'Free Delivery' },
                  { icon: '🔒', label: 'Secure Payment' },
                  { icon: '🔄', label: '7-Day Return' },
                ]?.map((badge) => (
                  <div key={badge?.label} className="flex items-center gap-2">
                    <span className="text-base">{badge?.icon}</span>
                    <span className="text-xs font-semibold text-gray-700">{badge?.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
